import React, { Component } from "react"
import PropTypes from "prop-types"

import LoadingSpinner from "components/LoadingSpinner"

import { filterHasValue } from "shared/search/helperFunctions"
import { MAX_NUM_SEARCH_RESULTS_SHOW_WARNING } from "imports/desktopConstants"

export default class VisualizationDisplay extends Component {
    static propTypes = {
        // from ListViewDisplayWrapper
        uniqueFlvId: PropTypes.string.isRequired,
        resource: PropTypes.object.isRequired,
        // from VisualizationScene
        uniqueVizSceneId: PropTypes.string.isRequired,
        getFilterValues: PropTypes.func,
        formatValuesForFilters: PropTypes.func,
        maxDataCount: PropTypes.number,
        // from connect
        data: PropTypes.object.isRequired,
        extraData: PropTypes.object.isRequired,
        currentFilters: PropTypes.object.isRequired,
        currentCount: PropTypes.number.isRequired,
        countIsLoading: PropTypes.bool.isRequired,
        visualizationFilterValues: PropTypes.any,
        isVisualizationRegistered: PropTypes.bool.isRequired,
        isVisualizationLoading: PropTypes.bool.isRequired,
        isVisualizationDataEmpty: PropTypes.bool.isRequired,
        serializationFunctions: PropTypes.object,
        secondaryGraphKeys: PropTypes.object,
        selectedValues: PropTypes.array.isRequired,
        storedGraphs: PropTypes.object.isRequired,
        updateFilter: PropTypes.func.isRequired,
        updateMultipleFilters: PropTypes.func.isRequired,
        registerVisualization: PropTypes.func.isRequired,
        loadVisualizationData: PropTypes.func.isRequired,
        setSecondaryGraphKeys: PropTypes.func.isRequired,
        updateVisualizationSelection: PropTypes.func.isRequired,
        clearVisualizationSelection: PropTypes.func.isRequired,
        // from VisualizationDisplays.js
        visualizationWrapper: PropTypes.func.isRequired,
        sidebarComponent: PropTypes.func.isRequired,
        visualizationKey: PropTypes.string.isRequired,
        shouldIgnoreFilters: PropTypes.bool.isRequired,
        shouldLoadDataFunc: PropTypes.func,
        modifyDataOnLoad: PropTypes.func,
        shouldDeferFilterApplication: PropTypes.bool.isRequired,
        defaultSecondaryKeys: PropTypes.array,
        variableRequestKey: PropTypes.string,
        visualizationFilterIds: PropTypes.array,
        sidebarTableLabel: PropTypes.string,
    }

    componentDidMount() {
        if (!this.props.isVisualizationRegistered) {
            this.props.registerVisualization(
                this.props.uniqueFlvId,
                this.props.uniqueVizSceneId,
                this.props.visualizationKey,
                this.props.variableRequestKey,
                this.props.visualizationFilterIds,
                this.props.shouldIgnoreFilters,
                this.props.initialExtraData,
            )
        }
        this.request = this.loadVisualizationData()
    }

    componentDidUpdate(oldProps) {
        const filtersChanged = !oldProps.currentFilters.equals(this.props.currentFilters)
        const keysChanged = !oldProps.secondaryGraphKeys.equals(this.props.secondaryGraphKeys)
        const countChanged = oldProps.currentCount !== this.props.currentCount

        // if the count is different and exceeds the max, cancel any existing
        // database requests and exist -- no need to update!
        if (countChanged && this.props.currentCount > this.props.maxDataCount) {
            this.cancelRequest()
            return
        }

        if (filtersChanged || keysChanged) {
            let shouldLoadData
            if (keysChanged) {
                shouldLoadData = true
            } else if (this.props.shouldLoadDataFunc) {
                shouldLoadData = this.props.shouldLoadDataFunc(oldProps, this.props)
            } else if (filtersChanged && this.props.shouldIgnoreFilters) {
                // if it's specified that we should ignore filters, we only want to update
                // the graph if non-graph relevant filters are changed.
                // (e.g. if the region filter is changed on the map, don't change the map)
                const { visualizationFilterIds } = this.props
                const filterKeys = [
                    ...this.props.currentFilters.keySeq().toJS(),
                    ...oldProps.currentFilters.keySeq().toJS(),
                ]

                const changedFilters = filterKeys.filter((key) => {
                    const newFilterValue = this.props.currentFilters.get(key)
                    const oldFilterValue = oldProps.currentFilters.get(key)

                    if (newFilterValue && oldFilterValue) {
                        return !newFilterValue.equals(oldFilterValue)
                    }
                    return newFilterValue !== oldFilterValue
                })

                shouldLoadData = changedFilters.some(
                    (uniqueFilterId) => !visualizationFilterIds.includes(uniqueFilterId),
                )
            } else {
                // this should only hit when filters have been changed
                // but there is no ignore requirement
                shouldLoadData = true
            }

            if (shouldLoadData) {
                this.cancelRequest()
                this.request = this.loadVisualizationData()
            }
        }
    }

    componentWillUnmount() {
        this.cancelRequest()
    }

    // secondary graph keys are things like `min_date` or `interval`
    // in trends graphs. The trend graph's primary key is `trends`,
    // but we can specify interval of the line via the sidebar.
    setSecondaryGraphKeys = (keys) => {
        this.props.setSecondaryGraphKeys(this.props.uniqueFlvId, this.props.uniqueVizSceneId, keys)
    }

    cancelRequest() {
        if (this.request) {
            this.request.abort()
        }
    }

    loadVisualizationData() {
        if (this.props.currentCount > this.props.maxDataCount && !this.props.countIsLoading) {
            return
        }

        // The visualization request should ignore certain filters on load:
        // for instance, no point in rendering a US map for Documents that are
        // filtered only on Alabama. So instead, the map should show the frequency
        // of documents everywhere -- we ignore the region filter, in the map case.
        const ignoredFilterIds = this.props.shouldIgnoreFilters ? this.props.visualizationFilterIds : []

        const additionalGraphKeys = this.props.variableRequestKey
            ? {
                  [this.props.variableRequestKey]: true,
                  ...this.props.secondaryGraphKeys.toJS(),
              }
            : this.props.secondaryGraphKeys.toJS()

        this.props.clearVisualizationSelection(this.props.uniqueFlvId, this.props.uniqueVizSceneId)

        return this.props.loadVisualizationData({
            resource: this.props.resource,
            uniqueFlvId: this.props.uniqueFlvId,
            uniqueVizSceneId: this.props.uniqueVizSceneId,
            visualizationKey: this.props.visualizationKey,
            additionalGraphKeys,
            ignoredFilterIds,
            modifyDataOnLoad: this.props.modifyDataOnLoad,
        })
    }

    // takes in an object of format "value", and converts it into something
    // processable in the store.
    updateFilter = (unformattedValue) => {
        const formattedFilters = this.props.formatValuesForFilters(unformattedValue)

        const serializedChanges = Object.entries(formattedFilters).reduce((acc, [uniqueFilterId, value]) => {
            const serializeFunc = this.props.serializationFunctions[uniqueFilterId]

            if (!serializeFunc) {
                return acc
            }

            const serializedValue = serializeFunc(value)
            const hasValue = filterHasValue(serializedValue)

            const filterInfo = {
                uniqueFilterId,
                value: serializedValue,
                hasValue,
            }

            return [...acc, filterInfo]
        }, [])

        // dispatch the action to update filters! If there's more than one filter,
        // call the 'update multiple filters' action creator, which will allow for
        // two filters to be changed at once instead of being called in two separate
        // dispatch calls.

        // There is a drawback to updateMultipleFilters: it won't function as you want it
        // to in the case of multifilters (it won't also update child filters). We
        // could possibly write that in later, but for now, all we're using updateMultipleFilters
        // for is dates.
        if (serializedChanges.length > 1) {
            this.props.updateMultipleFilters(this.props.uniqueFlvId, serializedChanges)
        } else if (serializedChanges.length === 1) {
            const filter = serializedChanges[0]
            this.props.updateFilter(filter.uniqueFilterId, this.props.uniqueFlvId, filter.value, filter.hasValue)
        }
    }

    updateSelection = (selection) => {
        this.props.updateVisualizationSelection(this.props.uniqueFlvId, this.props.uniqueVizSceneId, selection)
    }

    renderErrorScreen() {
        if (this.props.currentCount > this.props.maxDataCount) {
            const text = `This visualization is disabled for more than
                ${this.props.maxDataCount.toLocaleString()}
                ${DjangIO.app.models.QuorumDataType.by_value(this.props.quorumDataType).plural}.
                Try adding more filters to refine your dataset.`

            return (
                <div className="visualization-message-display">
                    <h3 className="no-load-warning">{text}</h3>
                </div>
            )
        } else if (this.props.isVisualizationDataEmpty) {
            return (
                <div className="visualization-message-display">
                    <h3 className="no-load-warning">
                        There are no results for this graph! Consider clearing some filters.
                    </h3>
                </div>
            )
        }
    }

    renderWrapper() {
        const VisualizationWrapper = this.props.visualizationWrapper
        return (
            <div className="visualization-wrapper" key="wrapper">
                {this.props.isVisualizationLoading && <LoadingSpinner id="search-viz-loading" />}
                {this.props.isVisualizationLoading && this.props.currentCount > MAX_NUM_SEARCH_RESULTS_SHOW_WARNING && (
                    <h3 className="viz-loading-slow-warning">
                        {`Results greater than ${MAX_NUM_SEARCH_RESULTS_SHOW_WARNING.toLocaleString()}
                            can take a few seconds.`}
                    </h3>
                )}
                <VisualizationWrapper
                    uniqueFlvId={this.props.uniqueFlvId}
                    uniqueVizSceneId={this.props.uniqueVizSceneId}
                    vizFilterId={this.props.filterId}
                    data={this.props.data}
                    isVisualizationLoading={this.props.isVisualizationLoading}
                    isVisualizationDataEmpty={this.props.isVisualizationDataEmpty}
                    selectedValues={this.props.selectedValues}
                    storedGraphs={this.props.storedGraphs}
                    visualizationFilterValues={this.props.visualizationFilterValues}
                    updateSelection={this.updateSelection}
                    updateFilter={this.updateFilter}
                    secondaryGraphKeys={this.props.secondaryGraphKeys}
                    configs={this.props.configs}
                    colorConfigs={this.props.colorConfigs || this.props.designColorConfigs}
                />
            </div>
        )
    }

    renderSidebar(disableButtons = false) {
        const SidebarComponent = this.props.sidebarComponent
        return (
            <div className="visualization-sidebar" key="sidebar">
                <SidebarComponent
                    uniqueFlvId={this.props.uniqueFlvId}
                    uniqueVizSceneId={this.props.uniqueVizSceneId}
                    vizFilterId={this.props.filterId}
                    isVisualizationLoading={this.props.isVisualizationLoading}
                    currentCount={this.props.currentCount}
                    updateFilter={this.updateFilter}
                    storedGraphs={this.props.storedGraphs}
                    visualizationFilterValues={this.props.visualizationFilterValues}
                    secondaryGraphKeys={this.props.secondaryGraphKeys}
                    setSecondaryGraphKeys={this.setSecondaryGraphKeys}
                    configs={this.props.configs}
                    sidebarTableLabel={this.props.sidebarTableLabel}
                    disableButtons={disableButtons}
                    noOrgDesign={this.props.noOrgDesign}
                    showCurrentToggle={this.props.showCurrentToggle}
                />
            </div>
        )
    }

    render() {
        const countExceeded = this.props.currentCount > this.props.maxDataCount && !this.props.countIsLoading
        const emptyData = this.props.isVisualizationDataEmpty && !this.props.isVisualizationLoading

        const shouldShowError = countExceeded || emptyData

        return (
            this.props.isVisualizationRegistered && (
                <div className="visualization-display">
                    {shouldShowError
                        ? [this.renderErrorScreen(), this.renderSidebar(true)]
                        : [this.renderWrapper(), this.renderSidebar()]}
                </div>
            )
        )
    }
}
